import { takeLatest, all, takeEvery } from 'redux-saga/effects';
import { takeOneByOne, takeOneByOneMany } from './sagaHelpers';
import { BasketActionTypes } from './basket';
import {
    loadBasketSaga,
    LoadBasketAction,
    loadDonationSaga,
    LoadDonationAction,
    updateBasketItemQuantitySaga,
    updateBasketItemQuantityQueueSaga,
    UpdateBasketItemQuantityAction,
    removeBasketItemSaga,
    removeBasketItemQueueSaga,
    RemoveBasketItemAction,
    addProductSaga,
    AddProductAction,
    addDonationSaga,
    AddDonationAction,
    addUpsellSaga,
    AddUpsellAction,
    addUpsellQueueSaga,
    calculateDeliveryCostSaga,
    calculateDeliveryCostQueueSaga,
    CalculateDeliveryCostAction,
    SetDeliveryTypeAction,
    setDeliveryTypeSaga,
    RefreshAvailableUpsellsAction,
    refreshAvailableUpsellsSaga,
    RemoveSofaglideAction,
    removeSofaglideSaga,
    removeSofaglideQueueSaga,
    updateSofashieldQueueSaga,
    UpdateSofashieldAction,
    updateSofashieldSaga,
    RemoveSofashieldAction,
    removeSofashieldSaga,
    removeSofashieldQueueSaga,
    removeAllSofashieldsQueueSaga,
    RemoveInvalidItemsAction,
    removeInvalidItemsSaga,
    InitBasketPageAction,
    initBasketPageSaga,
    removeBasketSaga,
    RemoveBasketAction,
    SaveCurrentPageAction,
    saveCurrentPageSaga,
    removeUserInfoSaga,
    RemoveUserInfoAction,
    HandleApiAuthErrorRequestAction,
    handleApiAuthErrorRequestSaga,
    removeAllInvalidBasketItemsQueueSaga,
    AddToBasketFromUrlSaga,
    AddToCartFromUrlAction
} from './basket/actions';
import {
    ValidateBasketReservationsAction,
    validateBasketReservationsSaga,
    ValidateCartAction,
    validateCartSaga
} from './basket/actions/validation';
import { PaymentActionTypes } from './payment';
import {
    changePaymentTypeSaga,
    ChangePaymentTypeAction,
    updateCardPaymentSaga,
    UpdateCardPaymentAction,
    UpdateIfcPaymentAction,
    updateIfcPaymentSaga,
    LoadPaymentAction,
    loadPaymentSaga,
    initializePaymentCalculatorSaga,
    InitializePaymentCalculatorAction,
    removePaymentResultSaga,
    RemovePaymentResultAction,
    CreatePaymentSessionAction,
    createPaymentSessionSaga,
    CardPaymentCompleteAction,
    cardPaymentCompleteSaga,
    CardPaymentRejectAction,
    cardPaymentRejectSaga
} from './payment/actions';
import {
    CheckEmailAction,
    checkEmailSaga,
    LoadDeliveryDetailsAction,
    loadDeliveryDetailsSaga,
    updateDeliveryDetailsSaga,
    UpdateDeliveryDetailsAction,
    UpdateDeliveryDetailsContactOptionsAction,
    updateDeliveryDetailsContactOptionsSaga,
    SetDeliverNotBeforeAction,
    setDeliverNotBeforeSaga,
    LoginProcessAction,
    loginProcessSaga,
    RegisterProcessAction,
    registerProcessSaga,
    InitCheckoutPageAction,
    initCheckoutPageSaga,
    processOrderSaga,
    ProcessOrderAction,
    InitOrderSummaryPageAction,
    initOrderSummaryPageSaga,
    PreprocessRedirectToNextStepAction,
    preprocessRedirectToNextStepSaga,
    InitPaymentPageAction,
    initPaymentPageSaga,
    TransferBasketAction,
    transferBasketSaga,
    InitCompleteResetPasswordPageAction,
    initCompleteResetPasswordPageSaga,
    preprocessContinueDeliveryDateSaga,
    PreprocessContinueDeliveryDateAction
} from './checkout/actions';
import { CheckoutActionTypes } from './checkout';
import {
    PaypalSetupPaymentAction,
    paypalSetupPaymentSaga,
    paypalRejectSaga,
    PaypalRejectAction,
    PaypalCompletePaymentAction,
    paypalCompletePaymentSaga,
    paypalSetAuthResultSaga,
    PaypalSetAuthResultAction
} from './payment/actions/paypal';
import { FindAddressAction, findAddressSaga } from './checkout/actions/addressSearchEditor';
import {
    InitFirstFinanceApplicationPageAction,
    initFirstFinanceApplicationPageSaga,
    loadQuestionSetSaga,
    LoadQuestionSetAction,
    InitIfcApplicationDataAction,
    initIfcApplicationDataSaga,
    SaveIfcFinanceDataAction,
    saveIfcFinanceDataSaga
} from './checkout/actions/finance';
import { ValidateBeforePaymentAction, validateBeforePaymentSaga } from './payment/actions/validateBeforePayment';

function* actionWatcher() {
    yield takeOneByOne<AddProductAction>(BasketActionTypes.ADD_PRODUCT, addProductSaga);
    yield takeEvery<AddDonationAction>(BasketActionTypes.ADD_DONATION, addDonationSaga);
    yield takeEvery<AddUpsellAction>(BasketActionTypes.ADD_UPSELL, addUpsellSaga);
    yield takeLatest<CalculateDeliveryCostAction>(BasketActionTypes.CALCULATE_DELIVERY_COST, calculateDeliveryCostSaga);
    yield takeEvery<LoadBasketAction>(BasketActionTypes.LOAD_BASKET, loadBasketSaga);
    yield takeLatest<InitBasketPageAction>(BasketActionTypes.INIT_BASKET_PAGE, initBasketPageSaga);
    yield takeLatest<LoadDonationAction>(BasketActionTypes.LOAD_DONATION, loadDonationSaga);
    yield takeLatest<SetDeliveryTypeAction>(BasketActionTypes.SET_DELIVERY_TYPE, setDeliveryTypeSaga);
    yield takeEvery<RemoveBasketItemAction>(BasketActionTypes.REMOVE_BASKET_ITEM, removeBasketItemSaga);
    yield takeEvery<RemoveSofaglideAction>(BasketActionTypes.REMOVE_SOFAGLIDE, removeSofaglideSaga);
    yield takeEvery<RemoveSofashieldAction>(BasketActionTypes.REMOVE_SOFASHIELD, removeSofashieldSaga);
    yield takeLatest<RemoveInvalidItemsAction>(BasketActionTypes.REMOVE_INVALID_ITEMS, removeInvalidItemsSaga);
    yield takeEvery<UpdateBasketItemQuantityAction>(BasketActionTypes.UPDATE_BASKET_ITEM_QUANTITY, updateBasketItemQuantitySaga);
    yield takeEvery<UpdateSofashieldAction>(BasketActionTypes.UPDATE_SOFASHIELD, updateSofashieldSaga);
    yield takeLatest<RefreshAvailableUpsellsAction>(BasketActionTypes.REFRESH_AVAILABLE_UPSELLS, refreshAvailableUpsellsSaga);
    yield takeOneByOneMany({
        [BasketActionTypes.UPDATE_BASKET_ITEM_QUANTITY_START]: updateBasketItemQuantityQueueSaga,
        [BasketActionTypes.REMOVE_BASKET_ITEM_START]: removeBasketItemQueueSaga,
        [BasketActionTypes.REMOVE_SOFAGLIDE_START]: removeSofaglideQueueSaga,
        [BasketActionTypes.UPDATE_SOFASHIELD_START]: updateSofashieldQueueSaga,
        [BasketActionTypes.REMOVE_SOFASHIELD_START]: removeSofashieldQueueSaga,
        [BasketActionTypes.ADD_UPSELL_START]: addUpsellQueueSaga,
        [BasketActionTypes.CALCULATE_DELIVERY_COST_START]: calculateDeliveryCostQueueSaga,
    });
    yield takeOneByOneMany({
        [BasketActionTypes.REMOVE_ALL_SOFASHIELDS]: removeAllSofashieldsQueueSaga,
        [BasketActionTypes.REMOVE_ALL_INVALID_ITEMS]: removeAllInvalidBasketItemsQueueSaga
    });
    yield takeLatest<SaveCurrentPageAction>(BasketActionTypes.SAVE_CURRENT_PAGE, saveCurrentPageSaga);
    yield takeLatest<ChangePaymentTypeAction>(PaymentActionTypes.CHANGE_PAYMENT_TYPE, changePaymentTypeSaga);
    yield takeLatest<LoadPaymentAction>(PaymentActionTypes.LOAD_PAYMENT, loadPaymentSaga);
    yield takeLatest<InitializePaymentCalculatorAction>(PaymentActionTypes.INITIALIZE_PAYMENT_CALCULATOR, initializePaymentCalculatorSaga);
    yield takeLatest<UpdateCardPaymentAction>(PaymentActionTypes.UPDATE_CARD_PAYMENT, updateCardPaymentSaga);
    yield takeLatest<UpdateIfcPaymentAction>(PaymentActionTypes.UPDATE_IFC_PAYMENT, updateIfcPaymentSaga);
    yield takeLatest<LoadDeliveryDetailsAction>(CheckoutActionTypes.LOAD_DELIVERY_DETAILS, loadDeliveryDetailsSaga);
    yield takeLatest<InitCheckoutPageAction>(CheckoutActionTypes.INIT_CHECKOUT_PAGE, initCheckoutPageSaga);
    yield takeLatest<InitOrderSummaryPageAction>(CheckoutActionTypes.INIT_ORDER_SUMMARY_PAGE, initOrderSummaryPageSaga);
    yield takeLatest<InitPaymentPageAction>(CheckoutActionTypes.INIT_PAYMENT_PAGE, initPaymentPageSaga);
    yield takeLatest<UpdateDeliveryDetailsAction>(CheckoutActionTypes.UPDATE_DELIVERY_DETAILS, updateDeliveryDetailsSaga);
    yield takeLatest<UpdateDeliveryDetailsContactOptionsAction>(CheckoutActionTypes.UPDATE_DELIVERY_DETAILS_CONTACT_OPTIONS, updateDeliveryDetailsContactOptionsSaga);
    yield takeEvery<SetDeliverNotBeforeAction>(CheckoutActionTypes.SET_DELIVER_NOT_BEFORE, setDeliverNotBeforeSaga);
    yield takeEvery<CheckEmailAction>(CheckoutActionTypes.CHECK_EMAIL, checkEmailSaga);
    yield takeEvery<LoginProcessAction>(CheckoutActionTypes.LOGIN_PROCESS, loginProcessSaga);
    yield takeEvery<RegisterProcessAction>(CheckoutActionTypes.REGISTER_PROCESS, registerProcessSaga);
    yield takeLatest<ProcessOrderAction>(CheckoutActionTypes.PROCESS_ORDER, processOrderSaga);
    yield takeLatest<RemovePaymentResultAction>(PaymentActionTypes.REMOVE_PAYMENT_RESULT, removePaymentResultSaga);
    yield takeLatest<RemoveBasketAction>(BasketActionTypes.REMOVE_BASKET, removeBasketSaga);
    yield takeLatest<PaypalSetupPaymentAction>(PaymentActionTypes.PAYPAL_SETUP_PAYMENT, paypalSetupPaymentSaga);
    yield takeLatest<PaypalSetAuthResultAction>(PaymentActionTypes.PAYPAL_SET_AUTH_RESULT, paypalSetAuthResultSaga);
    yield takeLatest<PaypalRejectAction>(PaymentActionTypes.PAYPAL_REJECT, paypalRejectSaga);
    yield takeLatest<PaypalCompletePaymentAction>(PaymentActionTypes.PAYPAL_COMPLETE_PAYMENT, paypalCompletePaymentSaga);
    yield takeEvery<FindAddressAction>(CheckoutActionTypes.FIND_ADDRESS, findAddressSaga);
    yield takeLatest<InitFirstFinanceApplicationPageAction>(CheckoutActionTypes.INIT_FIRST_FINANCE_APPLICATION_PAGE, initFirstFinanceApplicationPageSaga);
    yield takeLatest<PreprocessRedirectToNextStepAction>(CheckoutActionTypes.PREPROCESS_REDIRECT_TO_NEXT_STEP, preprocessRedirectToNextStepSaga);
    yield takeLatest<ValidateBasketReservationsAction>(BasketActionTypes.VALIDATE_BASKET_RESERVATIONS, validateBasketReservationsSaga);
    yield takeLatest<ValidateCartAction>(BasketActionTypes.VALIDATE_CART, validateCartSaga);
    yield takeEvery<RemoveUserInfoAction>(BasketActionTypes.REMOVE_USER_INFO, removeUserInfoSaga);
    yield takeLatest<HandleApiAuthErrorRequestAction>(BasketActionTypes.HANDLE_API_AUTH_ERROR_REQUEST, handleApiAuthErrorRequestSaga);
    yield takeLatest<TransferBasketAction>(CheckoutActionTypes.TRANSFER_BASKET, transferBasketSaga);
    yield takeLatest<InitCompleteResetPasswordPageAction>(CheckoutActionTypes.INIT_COMPLETE_RESET_PASSWORD_PAGE, initCompleteResetPasswordPageSaga);
    yield takeLatest<LoadQuestionSetAction>(CheckoutActionTypes.LOAD_QUESTION_SET, loadQuestionSetSaga);
    yield takeLatest<InitIfcApplicationDataAction>(CheckoutActionTypes.INIT_IFC_APPLICATION_DATA, initIfcApplicationDataSaga);
    yield takeLatest<SaveIfcFinanceDataAction>(CheckoutActionTypes.SAVE_IFC_FINANCE_DATA, saveIfcFinanceDataSaga);
    yield takeLatest<CreatePaymentSessionAction>(PaymentActionTypes.CREATE_PAYMENT_SESSION, createPaymentSessionSaga);
    yield takeLatest<CardPaymentCompleteAction>(PaymentActionTypes.CARD_PAYMENT_COMPLETE, cardPaymentCompleteSaga);
    yield takeLatest<CardPaymentRejectAction>(PaymentActionTypes.CARD_PAYMENT_REJECT, cardPaymentRejectSaga);
    yield takeLatest<PreprocessContinueDeliveryDateAction>(CheckoutActionTypes.CONTINUE_FROM_DELIVERY_DATE, preprocessContinueDeliveryDateSaga);
    yield takeLatest<ValidateBeforePaymentAction>(PaymentActionTypes.VALIDATE_BEFORE_PAYMENT, validateBeforePaymentSaga);
    yield takeLatest<AddToCartFromUrlAction>(BasketActionTypes.ADD_TO_CART_FROM_URL, AddToBasketFromUrlSaga);
}

export default function* rootSaga() {
    yield all([actionWatcher()]);
}